/**
 * Embed Bridge SDK
 *
 * Lightweight SDK for child apps to communicate with the SK Tools parent shell.
 * Protocol Version: 1.0
 */

const PROTOCOL_VERSION = '1.0';
const MESSAGE_SOURCE = {
  SHELL: 'sk-shell',
  APP: 'sk-app',
} as const;

export interface ShellUserContext {
  id: string;
  email: string;
  display_name?: string;
  is_super_admin: boolean;
}

export interface ShellTenantContext {
  id: string;
  slug: string;
  name?: string;
  role: string;
}

export interface ShellContext {
  user: ShellUserContext;
  tenant: ShellTenantContext;
}

export interface EmbedParams {
  isEmbedded: boolean;
  shellOrigin: string | null;
  token: string | null;
}

interface BridgeCallbacks {
  onContextUpdate?: (context: ShellContext) => void;
  onThemeChange?: (theme: 'light' | 'dark') => void;
  onNavigate?: (path: string) => void;
}

export interface BridgeAPI {
  cleanup: () => void;
  setTitle: (title: string) => void;
  navigateShell: (path: string) => void;
  showNotification: (
    type: 'success' | 'error' | 'info' | 'warning',
    message: string,
    duration?: number
  ) => void;
  reportError: (code: string, message: string, recoverable?: boolean) => void;
  /** Report a route change to the shell for deep link sync */
  reportRouteChange: (path: string, title?: string, replace?: boolean) => void;
}

/**
 * Extracts embed parameters from URL query string.
 *
 * For same-origin embedding (path-based routing), shell_origin is not provided
 * in the URL params since parent and child share the same origin.
 * In this case, we use window.location.origin as the shell origin.
 */
export function getEmbedParams(): EmbedParams {
  const params = new URLSearchParams(window.location.search);
  const isEmbedded = params.get('embed') === 'true';

  // For same-origin embedding, shell_origin won't be in URL params
  // since parent and child share the same origin
  const shellOrigin = params.get('shell_origin') ||
    (isEmbedded ? window.location.origin : null);

  return {
    isEmbedded,
    shellOrigin,
    token: params.get('token'),
  };
}

function sendToShell(payload: object, shellOrigin: string): void {
  const message = {
    source: MESSAGE_SOURCE.APP,
    version: PROTOCOL_VERSION,
    timestamp: Date.now(),
    payload,
  };
  window.parent.postMessage(message, shellOrigin);
}

function isValidShellMessage(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const msg = data as Record<string, unknown>;
  return (
    msg.source === MESSAGE_SOURCE.SHELL &&
    msg.version === PROTOCOL_VERSION &&
    typeof msg.payload === 'object' &&
    msg.payload !== null
  );
}

/**
 * Initializes the postMessage bridge with the parent shell.
 */
export function initBridge(
  shellOrigin: string,
  callbacks: BridgeCallbacks
): BridgeAPI {
  const handleMessage = (event: MessageEvent) => {
    if (event.origin !== shellOrigin) return;
    if (!isValidShellMessage(event.data)) return;

    const { payload } = event.data as { payload: { type: string; payload?: unknown } };

    switch (payload.type) {
      case 'SHELL_READY':
        sendToShell({ type: 'APP_READY' }, shellOrigin);
        break;
      case 'CONTEXT_UPDATE':
        callbacks.onContextUpdate?.(payload.payload as ShellContext);
        break;
      case 'THEME_CHANGE':
        callbacks.onThemeChange?.((payload.payload as { theme: 'light' | 'dark' }).theme);
        break;
      case 'NAVIGATE':
        callbacks.onNavigate?.((payload.payload as { path: string }).path);
        break;
      case 'TOKEN_REFRESH':
        break;
    }
  };

  window.addEventListener('message', handleMessage);
  sendToShell({ type: 'APP_READY' }, shellOrigin);

  return {
    cleanup: () => {
      window.removeEventListener('message', handleMessage);
    },
    setTitle: (title: string) => {
      sendToShell({ type: 'SET_TITLE', payload: { title } }, shellOrigin);
    },
    navigateShell: (path: string) => {
      sendToShell({ type: 'NAVIGATE_SHELL', payload: { path } }, shellOrigin);
    },
    showNotification: (
      type: 'success' | 'error' | 'info' | 'warning',
      message: string,
      duration?: number
    ) => {
      sendToShell(
        { type: 'SHOW_NOTIFICATION', payload: { type, message, duration } },
        shellOrigin
      );
    },
    reportError: (code: string, message: string, recoverable = true) => {
      sendToShell(
        { type: 'APP_ERROR', payload: { code, message, recoverable } },
        shellOrigin
      );
    },
    reportRouteChange: (path: string, title?: string, replace?: boolean) => {
      sendToShell(
        { type: 'ROUTE_CHANGE', payload: { path, title, replace } },
        shellOrigin
      );
    },
  };
}
